// src/pages/Checkout.js
import React, { useState, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';

const STRIPE_KEY = process.env.REACT_APP_STRIPE_LIVE_PUB_KEY;
const CREATE_PAYMENT_INTENT_URL =
  process.env.REACT_APP_CREATE_PAYMENT_INTENT_URL ||
  'https://us-central1-gladiators-website.cloudfunctions.net/createPaymentIntent';

if (!STRIPE_KEY) {
  console.error('Missing Stripe public key in .env');
}

const stripePromise = STRIPE_KEY ? loadStripe(STRIPE_KEY) : null;
const LOCAL_STORAGE_KEY = 'gladiator_customizations';

const CheckoutForm = ({ currentUser }) => {
  const { cartItems, clearCart } = useCart();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();

  const [form, setForm] = useState({
    name: '',
    email: '',
    street: '',
    city: '',
    state: '',
    zip: ''
  });

  const [processing, setProcessing] = useState(false);
  const [customizations, setCustomizations] = useState([]);

  // Sync saved or default customizations when cartItems load
  useEffect(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed.length === cartItems.length) {
        setCustomizations(parsed);
        return;
      }
    }

    setCustomizations(cartItems.map(() => ({
      addName: false,
      playerName: '',
      addNumber: false,
      playerNumber: ''
    })));
  }, [cartItems]);

  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(customizations));
  }, [customizations]);

  useEffect(() => {
    if (currentUser) {
      setForm(prev => ({
        ...prev,
        name: currentUser.displayName || '',
        email: currentUser.email || ''
      }));
    }
  }, [currentUser]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleCustomizationChange = (index, field, value) => {
    setCustomizations(prev => {
      const updated = [...prev];
      updated[index][field] = value;
      return updated;
    });
  };

  const getItemTotal = (item, customization) => {
    const base = item.price * item.quantity;
    let extra = 0;
    if (customization?.addName) extra += 4.00;
    if (customization?.addNumber) extra += 4.00;
    return base + extra;
  };

  const totalAmount = cartItems.reduce((sum, item, idx) => {
    return sum + getItemTotal(item, customizations[idx] || {});
  }, 0).toFixed(2);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);

    try {
      const response = await fetch(CREATE_PAYMENT_INTENT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: form.email,
          cartItems: cartItems.map((item, idx) => ({
            ...item,
            customization: customizations[idx] || {},
          })),
        }),
      });

      const { clientSecret, error } = await response.json();
      if (error || !clientSecret || !clientSecret.includes('_secret_')) {
        throw new Error('Invalid or missing client secret received.');
      }

      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: form.name,
            email: form.email,
            address: {
              line1: form.street,
              city: form.city,
              state: form.state,
              postal_code: form.zip
            }
          }
        }
      });

      if (stripeError) {
        alert(stripeError.message);
        setProcessing(false);
        return;
      }

      await addDoc(collection(db, 'orders'), {
        customerInfo: form,
        shippingAddress: {
          street: form.street,
          city: form.city,
          state: form.state,
          zip: form.zip
        },
        cartItems: cartItems.map((item, idx) => ({
          ...item,
          customization: customizations[idx] || {}
        })),
        totalAmount,
        stripePaymentIntentId: paymentIntent.id,
        createdAt: serverTimestamp(),
        userId: currentUser?.uid || null
      });

      clearCart();
      alert('Order placed successfully!');
      navigate('/');
    } catch (err) {
      console.error('Checkout error:', err);
      alert('Checkout failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ color: 'white', pointerEvents: processing ? 'none' : 'auto', opacity: processing ? 0.6 : 1 }}>
      <h2>{currentUser ? 'Parent Checkout' : 'Guest Checkout'}</h2>

      <div className="mb-3">
        <label style={{ color: 'white' }}>Full Name</label>
        <input name="name" placeholder="Full Name" value={form.name} onChange={handleChange} required className="form-control" />
      </div>
      <div className="mb-3">
        <label style={{ color: 'white' }}>Email</label>
        <input name="email" placeholder="Email" value={form.email} onChange={handleChange} required className="form-control" />
      </div>
      <div className="mb-3">
        <label style={{ color: 'white' }}>Street Address</label>
        <input name="street" placeholder="Street Address" value={form.street} onChange={handleChange} required className="form-control" />
      </div>
      <div className="mb-3">
        <label style={{ color: 'white' }}>City</label>
        <input name="city" placeholder="City" value={form.city} onChange={handleChange} required className="form-control" />
      </div>
      <div className="mb-3">
        <label style={{ color: 'white' }}>State</label>
        <input name="state" placeholder="State" value={form.state} onChange={handleChange} required className="form-control" />
      </div>
      <div className="mb-3">
        <label style={{ color: 'white' }}>Zip Code</label>
        <input name="zip" placeholder="Zip Code" value={form.zip} onChange={handleChange} required className="form-control" />
      </div>

      <div className="mb-3">
        <label style={{ color: 'white' }}>Card Details:</label>
        <div style={{ background: '#fff', padding: '1rem', borderRadius: '5px' }}>
          <CardElement />
        </div>
      </div>

      <h4>Order Summary</h4>

      {cartItems.map((item, idx) => {
        const customization = customizations[idx] || {};
        return (
          <div key={idx} style={{ display: 'flex', gap: '1rem', backgroundColor: '#000', padding: '0.75rem', borderRadius: '6px', marginBottom: '1rem', alignItems: 'center' }}>
            <img
              src={`/images/team-merch/${item.image}`}
              alt={item.name}
              style={{ width: 180, height: 180, objectFit: 'cover', borderRadius: '6px' }}
              onError={(e) => { e.target.src = '/images/default-placeholder.png'; }}
            />
            <div style={{ flex: 1 }}>
              <p><strong>{item.name}</strong> ({item.selectedSize})</p>
              <p>Qty: {item.quantity} × ${Number(item.price).toFixed(2)}</p>

              <div className="mb-3 p-3" style={{ backgroundColor: '#111', borderRadius: '6px', border: '1px solid #333' }}>
                <h6 style={{ color: '#FFD700' }}>Customization Options: Add your player's name and number to the back of any garment</h6>

                <div className="form-check">
                  <input className="form-check-input" type="checkbox" id={`nameCheck-${idx}`} checked={!!customization.addName} onChange={e => handleCustomizationChange(idx, 'addName', e.target.checked)} />
                  <label className="form-check-label" htmlFor={`nameCheck-${idx}`}>Add Player Name (+$4)</label>
                </div>
                {customization.addName && (
                  <input type="text" className="form-control mt-2" placeholder="Enter Name" value={customization.playerName} onChange={e => handleCustomizationChange(idx, 'playerName', e.target.value)} />
                )}

                <div className="form-check mt-3">
                  <input className="form-check-input" type="checkbox" id={`numberCheck-${idx}`} checked={!!customization.addNumber} onChange={e => handleCustomizationChange(idx, 'addNumber', e.target.checked)} />
                  <label className="form-check-label" htmlFor={`numberCheck-${idx}`}>Add Player Number (+$4)</label>
                </div>
                {customization.addNumber && (
                  <input type="text" className="form-control mt-2" placeholder="Enter Number" value={customization.playerNumber} onChange={e => handleCustomizationChange(idx, 'playerNumber', e.target.value)} />
                )}
              </div>

              <p><strong>Subtotal:</strong> ${getItemTotal(item, customization).toFixed(2)}</p>
            </div>
          </div>
        );
      })}

      <p><strong>Total: ${totalAmount}</strong></p>

      <p style={{ marginTop: '2.5rem', color: '#ccc', fontSize: '1.1rem', fontStyle: 'italic' }}>
        Once your order is complete, a member of the Gladiators team will contact you to arrange pickup or delivery of your items.
      </p>

      <button type="submit" className="btn btn-warning" disabled={!stripe || processing}>
        {processing ? 'Processing…' : 'Place Order'}
      </button>
    </form>
  );
};

const Checkout = () => {
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => setCurrentUser(user));
    return unsubscribe;
  }, []);

  return (
    <Elements stripe={stripePromise}>
      <CheckoutForm currentUser={currentUser} />
    </Elements>
  );
};

export default Checkout;
